import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, CalendarDays, Save } from "lucide-react";

const TYPES = [
  ["public_holiday", "Public Holiday"], ["campaign_day", "Campaign Day"], ["campaign_followup", "Campaign Follow-up"],
  ["mid_month_campaign", "Mid-Month Campaign"], ["payday_campaign", "Payday Campaign"],
  ["special_blocked", "Special Blocked Day"], ["other", "Other Event"],
];
const TYPE_LABEL = Object.fromEntries(TYPES);
const RULES = [
  ["block_mondays", "Block Mondays"], ["block_double_digit_campaign", "Block double-digit campaign days"],
  ["block_day_after_campaign", "Block day after campaign"], ["block_mid_month_campaign", "Block mid-month campaign"],
  ["block_day_25", "Block 25th"], ["block_day_26", "Block 26th"], ["block_day_after_public_holiday", "Block day after public holiday"],
];

export default function CalendarTab() {
  const { can } = useAuth();
  const canManage = can("hr:calendar_manage");
  const [year, setYear] = useState(new Date().getFullYear());
  const [events, setEvents] = useState([]);
  const [rules, setRules] = useState(null);
  const [form, setForm] = useState({ date: "", event_type: "public_holiday", title: "" });

  const load = () => api.get(`/hr/calendar?year=${year}`).then(r => setEvents(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year]);
  useEffect(() => { api.get("/hr/off-rules").then(r => setRules(r.data)).catch(() => {}); }, []);

  const add = async () => {
    if (!form.date) return toast.error("Pick a date");
    try { await api.post("/hr/calendar", form); toast.success("Event added"); setForm({ ...form, title: "" }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { try { await api.delete(`/hr/calendar/${id}`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const saveRules = async () => { try { await api.put("/hr/off-rules", rules); toast.success("Rules saved"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  return (
    <div className="grid grid-cols-[1fr_320px] gap-6" data-testid="hr-calendar-tab">
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-sky-600" />
            <Select value={String(year)} onValueChange={v => setYear(Number(v))}>
              <SelectTrigger className="h-9 w-28" data-testid="calendar-year-select"><SelectValue /></SelectTrigger>
              <SelectContent>{[year - 1, year, year + 1, year + 2].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        {canManage && (
          <div className="flex items-end gap-2 mb-3 rounded-lg border border-slate-200 bg-white p-3">
            <div><Label>Date</Label><Input type="date" data-testid="calendar-date-input" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="h-9" /></div>
            <div className="flex-1"><Label>Type</Label>
              <Select value={form.event_type} onValueChange={v => setForm({ ...form, event_type: v })}>
                <SelectTrigger className="h-9" data-testid="calendar-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex-1"><Label>Title</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="h-9" /></div>
            <Button onClick={add} data-testid="calendar-add-button" className="bg-sky-600 hover:bg-sky-700 h-9"><Plus className="w-4 h-4" /></Button>
          </div>
        )}
        <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
          <table className="excel-table w-full" data-testid="calendar-table">
            <thead><tr><th>Date</th><th>Type</th><th>Title</th><th></th></tr></thead>
            <tbody>
              {events.map(ev => (
                <tr key={ev.id} data-testid={`calendar-row-${ev.date}`}>
                  <td className="font-mono text-xs">{ev.date}</td>
                  <td><Badge variant="outline">{TYPE_LABEL[ev.event_type] || ev.event_type}</Badge></td>
                  <td>{ev.title}</td>
                  <td className="text-right">{canManage && <button onClick={() => del(ev.id)}><Trash2 className="w-4 h-4 text-rose-500" /></button>}</td>
                </tr>
              ))}
              {events.length === 0 && <tr><td colSpan={4} className="text-center text-slate-400 py-6">No events for {year}.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Off-day blocking rules */}
      <div>
        <h3 className="font-semibold mb-2">Packing Off-Day Rules</h3>
        <p className="text-xs text-slate-500 mb-3">Dates matching these rules block a packing staff's weekly off day.</p>
        {rules && (
          <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-2.5" data-testid="off-rules-panel">
            {RULES.map(([k, l]) => (
              <label key={k} className="flex items-center justify-between text-sm">
                <span className="text-slate-600">{l}</span>
                <Switch checked={!!rules[k]} onCheckedChange={v => setRules({ ...rules, [k]: v })} disabled={!canManage} data-testid={`rule-${k}`} />
              </label>
            ))}
            {canManage && <Button onClick={saveRules} data-testid="off-rules-save" className="w-full mt-2 bg-slate-900"><Save className="w-4 h-4 mr-1.5" />Save Rules</Button>}
          </div>
        )}
      </div>
    </div>
  );
}
